import type { AppState, CartStateData, MarketModalType, ProductVariant } from '../types';
import createStore from './create-store';

const initialState: AppState = {
  isMainNavOpen: false,
  isFiltersModalOpen: false,
  isSearchModalOpen: false,
  searchValue: '',
  isSearchRedirect: false,
  searchRedirectUrl: null,
  isCartModalOpen: false,
  isGiftboxModalOpen: false,
  isGiftcardInstructionsModalOpen: false,
  isSizeSelectorModalOpen: false,
  isDescriptionModalOpen: false,
  isQuickBuyModalOpen: false,
  isShopTheLookModalOpen: false,
  isGiftcardWarningModalOpen: false,
  quickBuyModalContent: null,
  isDetailsAndCareModalOpen: false,
  isSizeAndFitModalOpen: false,
  isSustainabilityModalOpen: false,
  isDeliveryAndReturnsModalOpen: false,
  previousMainNavPanelId: null,
  activeMainNavPanelId: null,
  cartIsUpdating: false,
  isOutOfStockModalOpen: false,
  outOfStockModalProduct: null,
  isNewsletterModalOpen: false,
  isMarketModalOpen: false,
  marketModalType: null,
  isPasswordModalOpen: false,
  cart: {
    data: null,
    revision: 0,
  },
};

const appStore = createStore(initialState, (setState, getState) => ({
  openMainNav(panelId: string): void {
    const previousMainNavPanelId = getState().activeMainNavPanelId;
    setState({
      isMainNavOpen: true,
      activeMainNavPanelId: panelId,
      previousMainNavPanelId,
    });
  },

  closeMainNav(): void {
    const previousMainNavPanelId = getState().activeMainNavPanelId;
    setState({
      isMainNavOpen: false,
      activeMainNavPanelId: null,
      previousMainNavPanelId,
    });
  },

  toggleMainNav(panelId: string): void {
    const { isMainNavOpen, activeMainNavPanelId } = getState();
    const shouldCloseCurrentPanel = isMainNavOpen && activeMainNavPanelId === panelId;

    if (shouldCloseCurrentPanel) {
      setState({
        isMainNavOpen: false,
        activeMainNavPanelId: null,
        previousMainNavPanelId: activeMainNavPanelId,
      });
      return;
    }

    setState({
      isMainNavOpen: true,
      activeMainNavPanelId: panelId,
      previousMainNavPanelId: activeMainNavPanelId,
    });
  },

  openFiltersModal(): void {
    setState({
      isFiltersModalOpen: true,
    });
  },

  closeFiltersModal(): void {
    setState({
      isFiltersModalOpen: false,
    });
  },

  toggleFiltersModal(): void {
    const { isFiltersModalOpen } = getState();
    setState({
      isFiltersModalOpen: !isFiltersModalOpen,
    });
  },

  openSearchModal(): void {
    setState({
      isSearchModalOpen: true,
    });
  },

  closeSearchModal(): void {
    setState({
      isSearchModalOpen: false,
      isSearchRedirect: false,
      searchRedirectUrl: null,
    });
  },

  toggleSearchModal(): void {
    const { isSearchModalOpen } = getState();
    setState({
      isSearchModalOpen: !isSearchModalOpen,
    });
  },

  updateSearchValue(value: string): void {
    setState({
      searchValue: value,
    });
  },

  setSearchRedirect(url: string | null): void {
    setState({
      isSearchRedirect: url !== null && url !== '',
      searchRedirectUrl: url,
    });
  },

  openCartModal(): void {
    setState({
      isCartModalOpen: true,
    });
  },

  closeCartModal(): void {
    setState({
      isCartModalOpen: false,
    });
  },

  toggleCartModal(): void {
    const { isCartModalOpen } = getState();
    setState({
      isCartModalOpen: !isCartModalOpen,
    });
  },

  openGiftboxModal(): void {
    setState({
      isGiftboxModalOpen: true,
    });
  },

  closeGiftboxModal(): void {
    setState({
      isGiftboxModalOpen: false,
    });
  },

  toggleGiftboxModal(): void {
    const { isGiftboxModalOpen } = getState();
    setState({
      isGiftboxModalOpen: !isGiftboxModalOpen,
    });
  },

  openGiftcardInstructionsModal(): void {
    setState({
      isGiftcardInstructionsModalOpen: true,
    });
  },

  closeGiftcardInstructionsModal(): void {
    setState({
      isGiftcardInstructionsModalOpen: false,
    });
  },

  toggleGiftcardInstructionsModal(): void {
    const { isGiftcardInstructionsModalOpen } = getState();
    setState({
      isGiftcardInstructionsModalOpen: !isGiftcardInstructionsModalOpen,
    });
  },

  openGiftcardWarningModal(): void {
    setState({
      isGiftcardWarningModalOpen: true,
    });
  },

  closeGiftcardWarningModal(): void {
    setState({
      isGiftcardWarningModalOpen: false,
    });
  },

  toggleGiftcardWarningModal(): void {
    const { isGiftcardWarningModalOpen } = getState();
    setState({
      isGiftcardWarningModalOpen: !isGiftcardWarningModalOpen,
    });
  },

  openSizeSelectorModal(): void {
    setState({
      isSizeSelectorModalOpen: true,
    });
  },

  closeSizeSelectorModal(): void {
    setState({
      isSizeSelectorModalOpen: false,
    });
  },

  toggleSizeSelectorModal(): void {
    const { isSizeSelectorModalOpen } = getState();
    setState({
      isSizeSelectorModalOpen: !isSizeSelectorModalOpen,
    });
  },

  openQuickBuyModal(content: DocumentFragment): void {
    setState({
      quickBuyModalContent: content,
      isQuickBuyModalOpen: true,
    });
  },

  closeQuickBuyModal(): void {
    setState({
      isQuickBuyModalOpen: false,
      quickBuyModalContent: null,
    });
  },

  toggleQuickBuyModal(): void {
    const { isQuickBuyModalOpen } = getState();
    setState({
      isQuickBuyModalOpen: !isQuickBuyModalOpen,
    });
  },

  openShopTheLookModal(): void {
    setState({
      isShopTheLookModalOpen: true,
    });
  },

  closeShopTheLookModal(): void {
    setState({
      isShopTheLookModalOpen: false,
    });
  },

  toggleShopTheLookModal(): void {
    const { isShopTheLookModalOpen } = getState();
    setState({
      isShopTheLookModalOpen: !isShopTheLookModalOpen,
    });
  },

  openOutOfStockModal(product: ProductVariant): void {
    setState({
      isOutOfStockModalOpen: true,
      outOfStockModalProduct: product,
    });
  },

  closeOutOfStockModal(): void {
    setState({
      isOutOfStockModalOpen: false,
    });
  },

  toggleOutOfStockModal(): void {
    const { isOutOfStockModalOpen } = getState();
    setState({
      isOutOfStockModalOpen: !isOutOfStockModalOpen,
    });
  },

  openDetailsAndCareModal(): void {
    setState({
      isDetailsAndCareModalOpen: true,
    });
  },

  closeDetailsAndCareModal(): void {
    setState({
      isDetailsAndCareModalOpen: false,
    });
  },

  toggleDetailsAndCareModal(): void {
    const { isDetailsAndCareModalOpen } = getState();
    setState({
      isDetailsAndCareModalOpen: !isDetailsAndCareModalOpen,
    });
  },

  openSizeAndFitModal(): void {
    setState({
      isSizeAndFitModalOpen: true,
    });
  },

  closeSizeAndFitModal(): void {
    setState({
      isSizeAndFitModalOpen: false,
    });
  },

  toggleSizeAndFitModal(): void {
    const { isSizeAndFitModalOpen } = getState();
    setState({
      isSizeAndFitModalOpen: !isSizeAndFitModalOpen,
    });
  },

  openSustainabilityModal(): void {
    setState({
      isSustainabilityModalOpen: true,
    });
  },

  closeSustainabilityModal(): void {
    setState({
      isSustainabilityModalOpen: false,
    });
  },

  toggleSustainabilityModal(): void {
    const { isSustainabilityModalOpen } = getState();
    setState({
      isSustainabilityModalOpen: !isSustainabilityModalOpen,
    });
  },

  openDeliveryAndReturnsModal(): void {
    setState({
      isDeliveryAndReturnsModalOpen: true,
    });
  },

  closeDeliveryAndReturnsModal(): void {
    setState({
      isDeliveryAndReturnsModalOpen: false,
    });
  },

  toggleDeliveryAndReturnsModal(): void {
    const { isDeliveryAndReturnsModalOpen } = getState();
    setState({
      isDeliveryAndReturnsModalOpen: !isDeliveryAndReturnsModalOpen,
    });
  },

  openDescriptionModal(): void {
    setState({
      isDescriptionModalOpen: true,
    });
  },

  closeDescriptionModal(): void {
    setState({
      isDescriptionModalOpen: false,
    });
  },

  toggleDescriptionModal(): void {
    const { isDescriptionModalOpen } = getState();
    setState({
      isDescriptionModalOpen: !isDescriptionModalOpen,
    });
  },

  setCart(data: CartStateData): void {
    const { cart } = getState();

    setState({
      cart: {
        ...cart,
        data,
        revision: cart.revision + 1,
      },
    });
  },

  setCartUpdating(isUpdating: boolean): void {
    setState({ cartIsUpdating: isUpdating });
  },

  openNewsletterModal(): void {
    setState({
      isNewsletterModalOpen: true,
    });
  },

  closeNewsletterModal(): void {
    setState({
      isNewsletterModalOpen: false,
    });
  },

  toggleNewsletterModal(): void {
    const { isNewsletterModalOpen } = getState();
    setState({
      isNewsletterModalOpen: !isNewsletterModalOpen,
    });
  },

  openMarketModal(): void {
    setState({
      isMarketModalOpen: true,
    });
  },

  closeMarketModal(): void {
    setState({
      isMarketModalOpen: false,
      marketModalType: null,
    });
  },

  updateMarketModalType(type: MarketModalType): void {
    setState({
      marketModalType: type,
    });
  },

  toggleMarketModal(): void {
    const { isMarketModalOpen } = getState();
    setState({
      isMarketModalOpen: !isMarketModalOpen,
    });
  },

  openPasswordModal(): void {
    setState({
      isPasswordModalOpen: true,
    });
  },

  closePasswordModal(): void {
    setState({
      isPasswordModalOpen: false,
    });
  },

  togglePasswordModal(): void {
    const { isPasswordModalOpen } = getState();
    setState({
      isPasswordModalOpen: !isPasswordModalOpen,
    });
  },
}));

export default appStore;
